import { useSite } from "../../context/SiteContext";
import { useLeadForm } from "../../hooks/useLeadForm";
import { Field, Input, Select, Textarea, Honeypot, SuccessState, SubmitButton } from "./fields";

const PLOTS = ["120 Sq. Yd.", "165 Sq. Yd.", "220 Sq. Yd.", "Undecided"];
const BUDGETS = ["Under ₹20L", "₹20–40L", "₹40–60L", "Above ₹60L", "Prefer not to say"];

export function EnquiryForm({ dark = false, compact = false }) {
  const { projects } = useSite();
  const f = useLeadForm({ source: "Enquiry", requireConsent: true, initial: { interested_project: "", plot_size: "", budget_range: "", preferred_date: "" } });

  if (f.status === "success") return <SuccessState dark={dark} testId="enquiry-success" text="Your enquiry has been received. Our team will contact you shortly." onReset={f.reset} />;

  return (
    <form onSubmit={f.submit} noValidate className={`relative grid gap-5 ${compact ? "" : "sm:grid-cols-2"}`} data-testid="enquiry-form">
      <Honeypot value={f.values.website} onChange={f.set("website")} />
      <Field label="Full Name" id="enquiry-full_name" required error={f.errors.full_name}>
        <Input id="enquiry-full_name" dark={dark} value={f.values.full_name} onChange={f.set("full_name")} placeholder="Your name" autoComplete="name" />
      </Field>
      <Field label="Mobile Number" id="enquiry-mobile" required error={f.errors.mobile}>
        <Input id="enquiry-mobile" dark={dark} type="tel" inputMode="numeric" value={f.values.mobile} onChange={f.set("mobile")} placeholder="10-digit mobile" autoComplete="tel" />
      </Field>
      <Field label="Email" id="enquiry-email" error={f.errors.email}>
        <Input id="enquiry-email" dark={dark} type="email" inputMode="email" value={f.values.email} onChange={f.set("email")} placeholder="you@example.com" autoComplete="email" />
      </Field>
      <Field label="Interested Project" id="enquiry-interested_project">
        <Select id="enquiry-interested_project" dark={dark} value={f.values.interested_project} onChange={f.set("interested_project")} placeholder="Select a project" options={projects.map((p) => ({ value: p.name, label: p.name }))} />
      </Field>
      <Field label="Preferred Plot Size" id="enquiry-plot_size">
        <Select id="enquiry-plot_size" dark={dark} value={f.values.plot_size} onChange={f.set("plot_size")} placeholder="Select size" options={PLOTS} />
      </Field>
      <Field label="Budget Range" id="enquiry-budget_range">
        <Select id="enquiry-budget_range" dark={dark} value={f.values.budget_range} onChange={f.set("budget_range")} placeholder="Select range" options={BUDGETS} />
      </Field>
      <Field label="Preferred Site Visit Date" id="enquiry-preferred_date">
        <Input id="enquiry-preferred_date" dark={dark} type="date" value={f.values.preferred_date} onChange={f.set("preferred_date")} min={new Date().toISOString().slice(0, 10)} />
      </Field>
      <Field label="Message" id="enquiry-message" className={compact ? "" : "sm:col-span-2"}>
        <Textarea id="enquiry-message" dark={dark} value={f.values.message} onChange={f.set("message")} placeholder="Tell us what you are looking for" rows={3} />
      </Field>
      <div className={compact ? "" : "sm:col-span-2"}>
        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <input type="checkbox" checked={f.values.consent} onChange={f.set("consent")} className="mt-1 h-4 w-4 accent-[#C9A961]" data-testid="enquiry-consent-checkbox" />
          <span className={dark ? "text-ivory/80" : "text-charcoal/80"}>I agree to be contacted regarding this enquiry.</span>
        </label>
        {f.errors.consent && <p className="mt-1.5 text-xs text-red-500" data-testid="enquiry-consent-error">{f.errors.consent}</p>}
      </div>
      {f.serverError && <p className={`${compact ? "" : "sm:col-span-2"} text-sm text-red-500`} data-testid="enquiry-server-error">{f.serverError}</p>}
      <div className={compact ? "" : "sm:col-span-2"}>
        <SubmitButton loading={f.status === "loading"} testId="enquiry-submit-button" className={dark ? "btn-gold !border-gold !text-gold hover:!text-emerald-900" : "btn-primary"}>Submit Enquiry</SubmitButton>
      </div>
    </form>
  );
}

export function SiteVisitForm({ dark = false, onDone }) {
  const f = useLeadForm({ source: "Site Visit", initial: { preferred_date: "" } });
  if (f.status === "success") return <SuccessState dark={dark} testId="site-visit-success" title="Visit request received." text="Our team will confirm your preferred date shortly." onReset={onDone || f.reset} />;
  return (
    <form onSubmit={f.submit} noValidate className="relative grid gap-5 sm:grid-cols-2" data-testid="site-visit-form">
      <Honeypot value={f.values.website} onChange={f.set("website")} />
      <Field label="Name" id="visit-full_name" required error={f.errors.full_name}>
        <Input id="visit-full_name" dark={dark} value={f.values.full_name} onChange={f.set("full_name")} placeholder="Your name" autoComplete="name" />
      </Field>
      <Field label="Mobile" id="visit-mobile" required error={f.errors.mobile}>
        <Input id="visit-mobile" dark={dark} type="tel" inputMode="numeric" value={f.values.mobile} onChange={f.set("mobile")} placeholder="10-digit mobile" autoComplete="tel" />
      </Field>
      <Field label="Email" id="visit-email" error={f.errors.email}>
        <Input id="visit-email" dark={dark} type="email" inputMode="email" value={f.values.email} onChange={f.set("email")} placeholder="you@example.com" />
      </Field>
      <Field label="Preferred Date" id="visit-preferred_date">
        <Input id="visit-preferred_date" dark={dark} type="date" value={f.values.preferred_date} onChange={f.set("preferred_date")} min={new Date().toISOString().slice(0, 10)} />
      </Field>
      <Field label="Message" id="visit-message" className="sm:col-span-2">
        <Textarea id="visit-message" dark={dark} value={f.values.message} onChange={f.set("message")} rows={3} placeholder="Anything we should know?" />
      </Field>
      {f.serverError && <p className="sm:col-span-2 text-sm text-red-500" data-testid="site-visit-server-error">{f.serverError}</p>}
      <div className="sm:col-span-2">
        <SubmitButton loading={f.status === "loading"} testId="site-visit-submit-button" className={dark ? "btn-gold !border-gold !text-gold hover:!text-emerald-900" : "btn-primary"}>Schedule My Visit</SubmitButton>
      </div>
    </form>
  );
}

export function ContactForm() {
  const f = useLeadForm({ source: "Contact" });
  if (f.status === "success") return <SuccessState testId="contact-success" text="Your message has been received. We will respond shortly." onReset={f.reset} />;
  return (
    <form onSubmit={f.submit} noValidate className="relative grid gap-5" data-testid="contact-form">
      <Honeypot value={f.values.website} onChange={f.set("website")} />
      <Field label="Name" id="contact-full_name" required error={f.errors.full_name}>
        <Input id="contact-full_name" value={f.values.full_name} onChange={f.set("full_name")} placeholder="Your name" autoComplete="name" />
      </Field>
      <Field label="Mobile" id="contact-mobile" required error={f.errors.mobile}>
        <Input id="contact-mobile" type="tel" inputMode="numeric" value={f.values.mobile} onChange={f.set("mobile")} placeholder="10-digit mobile" autoComplete="tel" />
      </Field>
      <Field label="Email" id="contact-email" error={f.errors.email}>
        <Input id="contact-email" type="email" inputMode="email" value={f.values.email} onChange={f.set("email")} placeholder="you@example.com" />
      </Field>
      <Field label="Message" id="contact-message">
        <Textarea id="contact-message" value={f.values.message} onChange={f.set("message")} placeholder="How can we help?" />
      </Field>
      {f.serverError && <p className="text-sm text-red-500" data-testid="contact-server-error">{f.serverError}</p>}
      <SubmitButton loading={f.status === "loading"} testId="contact-submit-button">Send Message</SubmitButton>
    </form>
  );
}

export function BrochureForm({ onDone }) {
  const { settings } = useSite();
  const f = useLeadForm({ source: "Brochure Download" });
  if (f.status === "success") {
    const url = settings.brochure_url;
    return (
      <div data-testid="brochure-success" className="text-center py-4">
        <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-gold text-gold">✓</span>
        <h3 className="h3">Thank you.</h3>
        {url ? (
          <>
            <p className="mt-3 text-sm text-charcoal/70">Your download should begin automatically. If not, use the link below.</p>
            <a href={url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} download target="_blank" rel="noreferrer" className="btn-primary mt-6" data-testid="brochure-download-link">Download Brochure</a>
            <iframe title="brochure" src={url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} className="hidden" />
          </>
        ) : (
          <p className="mt-3 text-sm text-charcoal/70" data-testid="brochure-pending-message">Thank you. The brochure will be shared with you on WhatsApp/email shortly.</p>
        )}
        <button type="button" onClick={onDone} className="mt-6 block mx-auto text-xs uppercase tracking-[0.2em] text-gold-600" data-testid="brochure-close-button">Close</button>
      </div>
    );
  }
  return (
    <form onSubmit={f.submit} noValidate className="relative grid gap-5" data-testid="brochure-form">
      <Honeypot value={f.values.website} onChange={f.set("website")} />
      <Field label="Name" id="brochure-full_name" required error={f.errors.full_name}>
        <Input id="brochure-full_name" value={f.values.full_name} onChange={f.set("full_name")} placeholder="Your name" autoComplete="name" />
      </Field>
      <Field label="Mobile" id="brochure-mobile" required error={f.errors.mobile}>
        <Input id="brochure-mobile" type="tel" inputMode="numeric" value={f.values.mobile} onChange={f.set("mobile")} placeholder="10-digit mobile" autoComplete="tel" />
      </Field>
      <Field label="Email" id="brochure-email" error={f.errors.email}>
        <Input id="brochure-email" type="email" inputMode="email" value={f.values.email} onChange={f.set("email")} placeholder="you@example.com" />
      </Field>
      {f.serverError && <p className="text-sm text-red-500" data-testid="brochure-server-error">{f.serverError}</p>}
      <SubmitButton loading={f.status === "loading"} testId="brochure-submit-button">Get the Brochure</SubmitButton>
    </form>
  );
}
